import React, { createContext, useContext, useEffect, useState } from "react";
import { Agent, Tool, GradingScript } from "@/types";
import { agentsApi } from "@/services/agents.api";
import { toolsApi } from "@/services/tools.api";
import { gradingApi } from "@/services/grading.api";

interface DataContextType {
  agents: Agent[];
  tools: Tool[];
  gradingScripts: GradingScript[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  refetchAgents: () => void;
  refetchTools: () => void;
  refetchGradingScripts: () => void;
}

const DataContext = createContext<DataContextType | null>(null);

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
}

interface DataProviderProps {
  children: React.ReactNode;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_KEYS = {
  agents: "eval_agents_cache",
  tools: "eval_tools_cache",
  gradingScripts: "eval_grading_cache",
};

function getCachedData<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_TTL;

    if (isExpired) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function setCachedData<T>(key: string, data: T): void {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
  } catch {
    // Ignore localStorage errors
  }
}

export function DataProvider({ children }: DataProviderProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [gradingScripts, setGradingScripts] = useState<GradingScript[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Try to load from cache first
      const cachedAgents = getCachedData<Agent[]>(CACHE_KEYS.agents);
      const cachedTools = getCachedData<Tool[]>(CACHE_KEYS.tools);
      const cachedGradingScripts = getCachedData<GradingScript[]>(
        CACHE_KEYS.gradingScripts,
      );

      if (cachedAgents && cachedTools && cachedGradingScripts) {
        // All data is cached and fresh
        setAgents(cachedAgents);
        setTools(cachedTools);
        setGradingScripts(cachedGradingScripts);
        setIsLoading(false);
        return;
      }

      // Load missing or expired data from API
      const [agentsData, toolsData, gradingData] = await Promise.all([
        cachedAgents
          ? Promise.resolve(cachedAgents)
          : agentsApi.list().catch(() => []),
        cachedTools
          ? Promise.resolve(cachedTools)
          : toolsApi.list().catch(() => []),
        cachedGradingScripts
          ? Promise.resolve(cachedGradingScripts)
          : gradingApi.list().catch(() => []),
      ]);

      // Update state
      setAgents(agentsData);
      setTools(toolsData);
      setGradingScripts(gradingData);

      // Cache the fresh data
      if (!cachedAgents) setCachedData(CACHE_KEYS.agents, agentsData);
      if (!cachedTools) setCachedData(CACHE_KEYS.tools, toolsData);
      if (!cachedGradingScripts)
        setCachedData(CACHE_KEYS.gradingScripts, gradingData);
    } catch (err) {
      console.error("Failed to load app data:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const refetchAgents = async () => {
    try {
      const agentsData = await agentsApi.list();
      setAgents(agentsData);
      setCachedData(CACHE_KEYS.agents, agentsData);
    } catch (err) {
      console.error("Failed to load agents:", err);
    }
  };

  const refetchTools = async () => {
    try {
      const toolsData = await toolsApi.list();
      setTools(toolsData);
      setCachedData(CACHE_KEYS.tools, toolsData);
    } catch (err) {
      console.error("Failed to load tools:", err);
    }
  };

  const refetchGradingScripts = async () => {
    try {
      const gradingData = await gradingApi.list();
      setGradingScripts(gradingData);
      setCachedData(CACHE_KEYS.gradingScripts, gradingData);
    } catch (err) {
      console.error("Failed to load grading scripts:", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const value: DataContextType = {
    agents,
    tools,
    gradingScripts,
    isLoading,
    error,
    refetch: loadData,
    refetchAgents,
    refetchTools,
    refetchGradingScripts,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
